import React, { useEffect, useState } from 'react';
import { TabItemProps } from '../../../../types/interfaces';
import Chips from '../../../../components/Chips/Chips';
import { toast } from 'react-toastify';
import CustomTable, { Column, IPageRequest } from '../../../../components/CustomTable';
import FilterButton from '../../../../components/FilterButton/FilterButton';
import DropdownMenu from '../../../../components/DropdownMenu';
import { OrderStatus, PaymentMethod } from '../../../../types/enums';
import { getCatalogueOrders, getMyOrders } from '../../../../services/catalogService';
import { useNavigate } from 'react-router-dom';
import RetryPaymentModal from './RetryPayment';

const OrdersPage = () => {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState<'Catalogue Orders' | 'My Orders'>('Catalogue Orders');
  const myOrdersCount = 5;
  const inProgressCount = 3;
  const [searchValue, setSearchValue] = useState('');
  const [currentPage, setCurrentPage] = useState(0);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [pageCount, setPageCount] = useState(1);
  const [orders, setOrders] = useState<any[]>([]);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [status, setStatus] = useState<OrderStatus | null>(null);
  const [isRetryModalOpen, setIsRetryModalOpen] = useState(false);
  const [selectedOrderId, setSelectedOrderId] = useState<string | null>(null);

  const pageRequest: IPageRequest = {
    pageNumber: currentPage + 1,
    pageSize: itemsPerPage,
  };

  const columns: Column[] = [
    { title: 'Order No', dataKey: 'orderNumber', type: 'string' },
    { title: 'Order Date', dataKey: 'orderDate', type: 'dateTime' },
    // { title: 'Order Name', dataKey: 'orderName', type: 'string' },
    { title: 'Payment Method', dataKey: 'paymentMethod', type: 'string' },
    { title: '$Price', dataKey: 'price', type: 'number' },
    { title: 'Shipment Method', dataKey: 'shippingMethod', type: 'status' },
    { title: 'Status', dataKey: 'orderStatus', type: 'status' },
    {
      title: 'Actions',
      dataKey: 'actions',
      type: 'action',
      actions: [
        {
          label: 'View',
          className: '',
          action: (item: any) => handleView(item),
          status: true,
        },
        {
          label: 'Retry Payment',
          className: 'text-green-500',
          action: (item: any) => handleRetryPaymentProof(item),
          status: (item: any) =>
            activeTab === 'My Orders' && item.orderStatus === OrderStatus.REJECTED,
        },
      ],
    },
  ];

  const handleRetryPaymentProof = (item: any) => {
    setSelectedOrderId(item.orderId);
    setIsRetryModalOpen(true);
  };

  const closeRetryModal = () => {
    setIsRetryModalOpen(false);
    setSelectedOrderId(null);
    fetchOrders(status, activeTab);
  };

  const handleView = (item: any) => {
    navigate(`/catalogue/order/${item.orderId}`, {
      state: {
        paymentMethod: item.paymentMethod,
        showApprovalActions: activeTab == 'Catalogue Orders' ? true : false,
      },
    });
  };

  const handleSearch = (value: string) => {
    setSearchValue(value);
    pageRequest.pageNumber = 1;
    fetchOrders(status, activeTab, value);
  };
  const handlePageChange = (selectedPage: number) => {
    setCurrentPage(selectedPage);
  };
  const handleTabClick = (tab: 'Catalogue Orders' | 'My Orders') => {
    // toast.info(tab);
    setActiveTab(tab);
    fetchOrders(status, tab);
  };

  const statusOptions = [
    { label: 'All', action: () => statusChange(null) },
    { label: 'Unpaid', action: () => statusChange(OrderStatus.UNPAID) },
    { label: 'In Progress', action: () => statusChange(OrderStatus.PROCESSING) },
    {
      label: 'Delivered',
      action: () => statusChange(OrderStatus.DELIVERED),
    },
    {
      label: 'Rejected',
      action: () => statusChange(OrderStatus.REJECTED),
    },
    {
      label: 'Confirm Orders',
      action: () => statusChange(OrderStatus.PAID),
    },
    {
      label: 'Cancelled',
      action: () => statusChange(OrderStatus.CANCELLED),
    },
  ];

  const statusChange = (value: OrderStatus | null) => {
    setStatus(value);
    pageRequest.pageNumber = 1;
    fetchOrders(value, activeTab);
  };

  const handleStatusClick = () => {
    setIsDropdownOpen((prev) => !prev);
  };

  const fetchOrders = (
    orderStatus: OrderStatus | null,
    tab: 'Catalogue Orders' | 'My Orders',
    searchValue: string | null = null
  ) => {
    const params = {
      ...pageRequest,
      ...(orderStatus !== null && { orderStatus }),
      ...(searchValue !== null && { searchValue }),
    };
    switch (tab) {
      case 'Catalogue Orders':
        fetchCatalaogOrders(params);
        break;
      case 'My Orders':
        fetchMyOrders(params);
        break;
      default:
        break;
    }
  };

  const fetchCatalaogOrders = async (params: any) => {
    const response = await getCatalogueOrders(params);
    setOrders(response?.data);
    if (response?.totalRecords) {
      setPageCount(Math.ceil(response.totalRecords / itemsPerPage));
    }
  };

  const fetchMyOrders = async (params: any) => {
    const response = await getMyOrders(params);
    setOrders(response?.data);
    if (response?.totalRecords) {
      setPageCount(Math.ceil(response.totalRecords / itemsPerPage));
    }
  };

  useEffect(() => {
    fetchOrders(status, activeTab);
  }, [currentPage, itemsPerPage]);

  return (
    <>
      <div className="rounded-lg bg-white px-10 py-6 shadow-md">
        <div className="flex items-center gap-6">
          {/* <p>Status</p> */}
          <div className="flex items-center gap-6">
            <div
              className={`${activeTab === 'Catalogue Orders' ? 'bg-primary' : 'bg-gray'} cursor-pointer rounded-lg border px-2 py-2`}
              onClick={() => handleTabClick('Catalogue Orders')}
            >
              <span>Catalogue Orders</span>
            </div>
            <div
              className={`${activeTab === 'My Orders' ? 'bg-primary' : 'bg-gray'} cursor-pointer rounded-lg border px-2 py-2`}
              onClick={() => handleTabClick('My Orders')}
            >
              <span className="mr-1">My Orders</span>
              {/* <span>({myOrdersCount})</span> */}
            </div>
            {/* <div
              className={`${activeTab === 'In Progress' ? 'bg-primary' : 'bg-gray'} border rounded-lg px-2 py-2 cursor-pointer`}
              onClick={() => handleTabClick('In Progress')}
            >
              <span className="mr-1">In Progress</span>
              <span>({inProgressCount})</span>
            </div>
            <div
              className={`${activeTab === 'Delivered' ? 'bg-primary' : 'bg-gray'} border rounded-lg px-2 py-2 cursor-pointer`}
              onClick={() => handleTabClick('Delivered')}
            >
              <span>Delivered</span>
            </div>
            <div
              className={`${activeTab === 'Confirm Orders' ? 'bg-primary' : 'bg-gray'} border rounded-lg px-2 py-2 cursor-pointer`}
              onClick={() => handleTabClick('Confirm Orders')}
            >
              <span>Confirm Orders</span>
            </div> */}
          </div>
        </div>
        {/* <div className="relative mt-2 flex justify-end">
          <FilterButton label="Status" onClick={handleStatusClick} />
          {isDropdownOpen && (
            <DropdownMenu
              open={isDropdownOpen}
              onClose={() => setIsDropdownOpen(false)}
              items={statusOptions}
            />
          )}
        </div> */}
        <div>
          <CustomTable
            data={orders}
            columns={columns}
            pageCount={pageCount} // Total page count for pagination
            showPagination={true} // Show pagination control
            showSearch={true} // Show search bar
            onSearch={handleSearch} // Search function
            showAddButton={false} // Add button (set to false here)
            showTitle={false}
            onPageChange={(selectedPage) => handlePageChange(selectedPage)}
            showFilterIcon={false}
            showStatusFilter={true}
            statusOptions={statusOptions}
          />
        </div>

        {isRetryModalOpen && selectedOrderId && (
          <RetryPaymentModal
            orderId={selectedOrderId}
            onClose={closeRetryModal}
            onRetrySuccess={closeRetryModal}
            paymentMethod={PaymentMethod.OFFLINE}
          />
        )}
      </div>
    </>
  );
};

export default OrdersPage;
